import React, {
	ChangeEvent,
	Dispatch,
	useCallback,
	useEffect,
	useState,
} from "react";
import { DatePicker, Icon, Select } from "@alm/flow-ds/components";
import { Actions, createFilter, setQueryFilter } from "./finderReducer";
import { DateElementDefinition, operators } from "lib/api/types";
import { useTranslation } from "react-i18next";
import jsonpointer from "lib/jsonpointer/jsonpointer";

type Props = DateElementDefinition & {
	index: number;
	dispatch: Dispatch<Actions>;
	queryValue?: string;
};

function DateSearchField({ index, id, dispatch, label, queryValue }: Props) {
	const { t } = useTranslation();
	const [valueA, setValueA] = useState(
		queryValue === undefined || queryValue?.includes("<")
			? ""
			: queryValue.length === 10
			? queryValue
			: queryValue.includes(">")
			? queryValue.substring(1)
			: queryValue.substring(0, 10)
	);
	const [valueB, setValueB] = useState(
		queryValue === undefined ||
			queryValue?.length === 10 ||
			queryValue?.includes(">")
			? ""
			: queryValue.includes("<")
			? queryValue.substring(1)
			: queryValue.substring(12)
	);
	const [operator, setOperator] = useState(
		queryValue === undefined
			? "exact"
			: queryValue.length === 10
			? "exact"
			: queryValue.length > 12
			? "between"
			: queryValue.includes("<")
			? "lt"
			: "gt"
	);

	const onOperatorChange = useCallback(
		(event: ChangeEvent<HTMLSelectElement>) => {
			setOperator(event.currentTarget.value);
		},
		[]
	);

	useEffect(() => {
		let clause;
		let summary;

		if (operator === operators.exact) {
			clause = createFilter(jsonpointer.pointerToId(id), operator, {
				value: valueA,
			});
			summary = valueA;
		} else if (operator === "between") {
			clause = createFilter(
				jsonpointer.pointerToId(id),
				operators.range,
				{
					gt: valueA,
					lt: valueB,
				}
			);
			summary = `${valueA} - ${valueB}`;
		} else if (operator === "gt") {
			clause = createFilter(
				jsonpointer.pointerToId(id),
				operators.range,
				{
					gt: valueA,
				}
			);
			summary = `>${valueA}`;
		} else {
			clause = createFilter(
				jsonpointer.pointerToId(id),
				operators.range,
				{
					lt: valueB,
				}
			);
			summary = `<${valueB}`;
		}

		dispatch(setQueryFilter(clause, index, summary));
	}, [dispatch, id, index, operator, valueA, valueB]);

	return (
		<>
			<div className="col-6 col-md-2 qbf-field--date">
				<Select
					value={operator}
					label={t("elements.finder.selectOperatorLabel")}
					labelHidden={true}
					onChange={onOperatorChange}>
					<option value="exact">{t("elements.finder.exact")}</option>
					<option value="gt">{t("elements.finder.after")}</option>
					<option value="lt">{t("elements.finder.before")}</option>
					<option value="between">
						{t("elements.finder.between")}
					</option>
				</Select>
			</div>

			<div className="col-12 col-md-5 col-lg-6 col-xl-4 mt-3 mt-md-0">
				<div className="d-flex">
					<div className="">
						{operator !== "lt" && (
							<DatePicker
								modal
								label={label} // TODO: dynamic labels depending on the operator
								value={valueA}
								onChange={setValueA}
								onBlur={setValueA}
								locale={"en"}
								labelHidden={true}
								id={`qbf-input-${index}`}
							/>
						)}
					</div>
					{operator === "between" && (
						<div className="">
							<Icon
								type="arrow-right"
								className="qbf-date__between-arrow"
							/>
						</div>
					)}
					{operator !== "exact" && operator !== "gt" && (
						<div className="">
							<DatePicker
								modal
								label={label}
								value={valueB}
								onChange={setValueB}
								onBlur={setValueB}
								locale={"en"}
								labelHidden={true}
								id={`qbf-input-${index}`}
							/>
						</div>
					)}
				</div>
			</div>
		</>
	);
}

export default DateSearchField;
